'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/redux/store';
import { answerQuestion, setCurrentQuestionIndex } from '@/redux/reducers/questionnaireSlice';
import {
  selectCurrentQuestion,
  selectCurrentQuestionIndex,
} from '@/redux/selectors/questionnaireSelectors';
import Button from '../Button/Button';
import StressTestModal from '../Modal/StressTestModal';
import { useUser, useAuth } from '@clerk/nextjs';
import { fetchStressLevelData } from '@/redux/reducers/stressLevelSlice';
import type { Question } from '@/types/stresstest';
import { T } from '@tolgee/react';
import { useStressTestService } from '@/hooks/useStressTestService';

const Hero: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useUser();
  const { getToken } = useAuth();
  const currentQuestion = useSelector(selectCurrentQuestion);
  const currentQuestionIndex = useSelector(selectCurrentQuestionIndex);
  const { submitTestResult } = useStressTestService();
  const totalQuestions = useSelector((state: RootState) => state.questionnaire.questions.length);
  const answers = useSelector((state: RootState) => state.questionnaire.answers);

  const isCompleted = useSelector((state: RootState) => state.questionnaire.isCompleted);

  const [firstQuestion, setFirstQuestion] = useState<Question | null>(null);
  const [firstQuestionIndex, setFirstQuestionIndex] = useState<number | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [score, setScore] = useState<number | undefined>(undefined);

  const handleAnswer = (answer: number) => {
    if (currentQuestionIndex <= totalQuestions - 1 && !isCompleted) {
      dispatch(answerQuestion({ questionId: currentQuestion.id, answer }));
      if (currentQuestionIndex < totalQuestions) {
        setIsModalOpen(true);
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      dispatch(setCurrentQuestionIndex(currentQuestionIndex - 1));
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!firstQuestion && currentQuestion) {
      setFirstQuestion(currentQuestion);
      setFirstQuestionIndex(currentQuestionIndex);
    }
  }, [currentQuestion, currentQuestionIndex, firstQuestion]);

  useEffect(() => {
    const handleCompletedTest = async () => {
      if (isCompleted && user) {
        const totalScore = Object.values(answers).reduce((acc, answer) => acc + answer, 0);
        setScore(totalScore);
        await submitTestResult({ score: totalScore });
        const token = await getToken();
        if (token) {
          void dispatch(fetchStressLevelData({ userId: user.id, token }));
        }
      }
    };

    void handleCompletedTest();
  }, [isCompleted, answers, user, submitTestResult, dispatch, getToken]);

  useEffect(() => {
    const fetchStressData = async () => {
      if (user?.id) {
        const token = await getToken();
        if (token) {
          void dispatch(fetchStressLevelData({ userId: user.id, token }));
        }
      }
    };

    void fetchStressData();
  }, [dispatch, user?.id, getToken]);

  return (
    <div className="bg-primary p-6 h-[620px]">
      <div className="flex flex-col lg:flex-row rounded-2xl overflow-hidden h-full">
        {/* Left Section */}
        <div className="relative flex-1 m-0 p-0">
          <Image
            src="/img/home/stresstestcircle.svg"
            alt="Stress Test"
            className="w-full h-full object-cover"
            fill
            priority={true}
          />
          <div className="absolute top-11 left-11 bg-white/50 p-2.5 backdrop-blur-[10px] text-primary px-4 py-1 rounded-full uppercase">
            <T>stresstest.hero.tag.label</T>
          </div>
          <div className="absolute bottom-11 left-11 text-white text-4xl md:text-6xl w-3/4">
            <T>stresstest.hero.title.label</T>
          </div>
        </div>
        {/* Right Section */}
        <div className="flex-1 p-8 relative bg-white">
          <div className="text-sm uppercase">
            {firstQuestionIndex || 0 + 1}/21 <T>stresstest.question-count.label</T>
          </div>
          <div className="mt-4 text-4xl w-full md:w-1/2">
            <T>{firstQuestion?.question || ''}</T>
          </div>
          <div className="mt-8 block md:flex space-x-0 md:space-x-4 relative md:absolute bottom-0 md:bottom-11 left-0 px-0 md:px-11 w-full">
            <div className="mt-4 md:mt-0 w-full">
              <Button variant="secondary" onClick={() => handleAnswer(1)}>
                <T>stresstest.button.agree.label</T>
              </Button>
            </div>
            <div className="mt-4 md:mt-0 w-full">
              <Button variant="secondary" onClick={() => handleAnswer(0)}>
                <T>stresstest.button.disagree.label</T>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <StressTestModal
        isOpen={isModalOpen}
        onClose={closeModal}
        currentQuestion={currentQuestionIndex + 1}
        totalQuestions={totalQuestions}
        questionText={currentQuestion?.question || ''}
        isCompleted={isCompleted}
        onAnswer={handleAnswer}
        onPrevious={handlePrevious}
        score={score}
      />
    </div>
  );
};

export default Hero;
